import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiErrorCode, GEOFENCE_ERROR_MESSAGES, NAME_MAX } from '@/lib/geofences';

// Aligné sur nameSchema / description (rules-engine, routes/geofences/geofence.shared.ts)
const schema = z.object({
  name: z.string().trim().min(1, 'Nom requis').max(NAME_MAX, `${NAME_MAX} caractères maximum`),
  description: z.string().trim().max(500, '500 caractères maximum'),
});

export type GeofenceNameValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  title: string;
  description?: string;
  submitLabel: string;
  initial?: Partial<GeofenceNameValues>;
  pending: boolean;
  /** Rejet DUPLICATE_NAME → erreur sur le champ nom ; tout autre rejet → onError. */
  onSubmit: (values: GeofenceNameValues) => Promise<unknown>;
  onError: (err: unknown) => void;
  onCancel: () => void;
}

export function GeofenceNameDialog({ open, title, description, submitLabel, initial, pending, onSubmit, onError, onCancel }: Props) {
  const { register, handleSubmit, reset, setError, formState: { errors } } =
    useForm<GeofenceNameValues>({ resolver: zodResolver(schema), defaultValues: { name: '', description: '' } });

  useEffect(() => {
    if (open) reset({ name: initial?.name ?? '', description: initial?.description ?? '' });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (values: GeofenceNameValues) => {
    try {
      await onSubmit(values);
    } catch (err) {
      if (apiErrorCode(err) === 'DUPLICATE_NAME') setError('name', { message: GEOFENCE_ERROR_MESSAGES['DUPLICATE_NAME'] });
      else onError(err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <div>
            <Label htmlFor="geofence-name">Nom *</Label>
            <Input id="geofence-name" {...register('name')} maxLength={NAME_MAX} placeholder="Port de Conakry" aria-invalid={!!errors.name} />
            {errors.name && <p role="alert" className="text-xs text-danger mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <Label htmlFor="geofence-description">Description</Label>
            <textarea
              autoComplete="off"
              id="geofence-description"
              {...register('description')}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {errors.description && <p className="text-xs text-danger mt-1">{errors.description.message}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onCancel}>Annuler</Button>
            <Button type="submit" disabled={pending}>{pending ? 'Enregistrement…' : submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
